import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./ManageConsultants.css";
import { GetDataFromApiWithParams, DeleteAndGetResponse, UpdateAndGetResponse } from "../../../../../../services/api/ExecuteApiRequests";
import { 
  Box, 
  Button, 
  Alert, 
  CircularProgress, 
  Typography, 
  Card, 
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Modal,
  Grid,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { Add, Edit, Delete, Save, Cancel, Download, Close } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ManageConsultants = () => {
  const [consultants, setConsultants] = useState([]);
  const [editingConsultant, setEditingConsultant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Consultant grades
  const grades = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  // Consultant types
  const consultantTypes = [
    "Architectural Consultant",
    "Structural Consultant",
    "Electrical Consultant",
    "Mechanical Consultant",
    "Sanitary Consultant",
    "ElectroMechnical Consultant",
    "General Consultant",
    "Environmental Consultant",
    "Project Management Consultant",
    "Engineering Consultant"
  ];

  const fetchConsultants = async () => {
    try {
      setLoading(true);
      setError("");
      const responseData = await GetDataFromApiWithParams('consultants');
      console.log("Fetched consultants:", responseData);
      setConsultants(responseData);
    } catch (error) {
      console.error("Error fetching consultants:", error);
      toast.error("Error fetching consultants: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {    
    fetchConsultants();
  }, []);

  const handleEdit = (consultant) => {
    console.log("Editing consultant:", consultant);
    
    setEditingConsultant({
      id: consultant.id || "",
      name: consultant.name || "",
      class: consultant.class ? consultant.class.toString() : "1",
      license_no: consultant.license_no || "",
      tin_no: consultant.tin_no || "",
      origin_country: consultant.origin_country || "Ethiopia",
      address_region: consultant.address_region || "",
      address_city: consultant.address_city || "",
      address_subcity: consultant.address_subcity || "",
      address_woreda: consultant.address_woreda || "",
      address_house_no: consultant.address_house_no || "",
      contact_person_name: consultant.contact_person_name || "",
      telephone: consultant.telephone || "",
      email: consultant.email || "",
      description: consultant.description || "",
      consultant_type: consultant.consultant_type || "",
      experience_year: consultant.experience_year || "",
    });
    setEditModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this consultant?")) {
      try {
        await DeleteAndGetResponse(`consultants/${id}`);
        toast.success("Consultant deleted successfully!");
        fetchConsultants();
      } catch (error) {
        console.error("Error deleting consultant:", error);
        toast.error("Error deleting consultant: " + (error.message || "Unknown error"));
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!editingConsultant) return;

    console.log("Saving consultant data:", editingConsultant);

    if (!editingConsultant.name) {
      toast.error("Company name is required");
      return;
    }

    if (!editingConsultant.class) {
      toast.error("Class is required");
      return;
    }

    if (!editingConsultant.license_no) {
      toast.error("License number is required");
      return;
    }

    if (!editingConsultant.tin_no) {
      toast.error("TIN number is required");
      return;
    }

    if (!editingConsultant.consultant_type) {
      toast.error("Consultant type is required");
      return;
    }

    if (!editingConsultant.experience_year) {
      toast.error("Years of experience is required");
      return;
    }

    setSubmitting(true);

    try {
      await UpdateAndGetResponse(`/consultants/${editingConsultant.id}`, {
        name: editingConsultant.name,
        cls: editingConsultant.class,
        license_no: editingConsultant.license_no,
        tin_no: editingConsultant.tin_no,
        origin_country: editingConsultant.origin_country,
        address_region: editingConsultant.address_region,
        address_city: editingConsultant.address_city,
        address_subcity: editingConsultant.address_subcity,
        address_woreda: editingConsultant.address_woreda,
        address_house_no: editingConsultant.address_house_no,
        contact_person_name: editingConsultant.contact_person_name,
        telephone: editingConsultant.telephone,
        email: editingConsultant.email,
        description: editingConsultant.description,
        consultant_type: editingConsultant.consultant_type,
        experience_year: editingConsultant.experience_year,
        id: editingConsultant.id,
      });
      
      toast.success("Consultant updated successfully!");
      setEditModalOpen(false);
      setEditingConsultant(null);
      fetchConsultants();
    } catch (error) {
      console.error("Error updating consultant:", error);
      toast.error("Error updating consultant: " + (error.message || "Unknown error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingConsultant(null);
    setEditModalOpen(false);
  };

  const handleExportAll = () => {
    try {
      if (consultants.length === 0) {
        toast.error("No data to export");
        return;
      }

      const headers = [
        'ID', 'Company Name', 'Class', 'Consultant Type', 'Years of Experience',
        'License No', 'TIN No', 'Origin Country', 'Address Region', 'City', 
        'Sub-City', 'Woreda', 'House No', 'Contact Person', 'Telephone', 
        'Email', 'Description'
      ];
      
      const csvHeaders = headers.join(',');
      
      const csvRows = consultants.map(consultant => {
        const row = [
          consultant.id,
          `"${consultant.name || ''}"`,
          consultant.class,
          `"${consultant.consultant_type || ''}"`,
          consultant.experience_year || '',
          consultant.license_no,
          consultant.tin_no,
          consultant.origin_country,
          consultant.address_region,
          consultant.address_city,
          consultant.address_subcity,
          consultant.address_woreda,
          consultant.address_house_no,
          `"${consultant.contact_person_name || ''}"`,
          consultant.telephone,
          consultant.email,
          `"${consultant.description || ''}"`
        ];
        return row.join(',');
      }).join('\n');
      
      const csvContent = `${csvHeaders}\n${csvRows}`;
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `consultants_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${consultants.length} consultants successfully!`);
    } catch (err) {
      console.error("Error exporting to CSV:", err);
      toast.error("Error exporting consultants data");
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 1200,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    overflow: 'auto',
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading consultants...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Manage Consultants ({consultants.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportAll}
                disabled={consultants.length === 0}
              >
                Export All
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                component={Link}
                to="/registerConsultant"
              >
                New Consultant
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          {consultants.length > 0 ? (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Consultant Type</TableCell>
                    <TableCell>Years Exp</TableCell>
                    <TableCell>License No</TableCell>
                    <TableCell>TIN No</TableCell>
                    <TableCell>Origin Country</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Sub-City</TableCell>
                    <TableCell>Woreda</TableCell>
                    <TableCell>House No</TableCell>
                    <TableCell>Contact Person</TableCell>
                    <TableCell>Telephone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consultants.map((consultant) => (
                    <TableRow key={consultant.id} hover>
                      <TableCell>{consultant.id}</TableCell>
                      <TableCell>{consultant.name}</TableCell>
                      <TableCell>{consultant.class}</TableCell>
                      <TableCell>{consultant.consultant_type || '-'}</TableCell>
                      <TableCell>{consultant.experience_year || '-'}</TableCell>
                      <TableCell>{consultant.license_no}</TableCell>
                      <TableCell>{consultant.tin_no}</TableCell>
                      <TableCell>{consultant.origin_country}</TableCell>
                      <TableCell>{consultant.address_region}</TableCell>
                      <TableCell>{consultant.address_city}</TableCell>
                      <TableCell>{consultant.address_subcity}</TableCell>
                      <TableCell>{consultant.address_woreda}</TableCell>
                      <TableCell>{consultant.address_house_no}</TableCell>
                      <TableCell>{consultant.contact_person_name}</TableCell>
                      <TableCell>{consultant.telephone}</TableCell>
                      <TableCell>{consultant.email}</TableCell>
                      <TableCell sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                        {consultant.description}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEdit(consultant)}
                            title="Edit"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(consultant.id)}
                            title="Delete"
                            disabled
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            !error && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  No consultants found.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Click "New Consultant" to add your first consultant.
                </Typography>
              </Box>
            )
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        onClose={handleCancelEdit}
        aria-labelledby="edit-consultant-modal"
      >
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2">
              Edit Consultant (ID: {editingConsultant?.id})
            </Typography>
            <IconButton onClick={handleCancelEdit} size="small">
              <Close />
            </IconButton>
          </Box>
          
          {editingConsultant && (
            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
              <Grid container spacing={2}>
                {/* Row 1: Company Name, Class, Consultant Type */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={editingConsultant.name || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, name: e.target.value})}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Consultant Grade</InputLabel>
                    <Select
                      value={editingConsultant.class || ''}
                      onChange={(e) => setEditingConsultant({...editingConsultant, class: e.target.value})}
                      label="Consultant Grade"
                    >
                      {grades.map((grade) => (
                        <MenuItem key={grade} value={grade.toString()}>
                          Grade {grade}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Consultant Type</InputLabel>
                    <Select
                      value={editingConsultant.consultant_type || ''}
                      onChange={(e) => setEditingConsultant({...editingConsultant, consultant_type: e.target.value})}
                      label="Consultant Type"
                    >
                      {consultantTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Row 2: Years of Experience, License No, TIN No */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Years of Experience"
                    type="number"
                    value={editingConsultant.experience_year || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, experience_year: e.target.value})}
                    size="small"
                    required
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="License No"
                    value={editingConsultant.license_no || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, license_no: e.target.value})}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="TIN No"
                    value={editingConsultant.tin_no || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, tin_no: e.target.value})}
                    size="small"
                    
                  />
                </Grid>

                {/* Row 3: Origin Country, Telephone, Email */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Origin Country"
                    value={editingConsultant.origin_country || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, origin_country: e.target.value})}
                    size="small"
                    
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Telephone"
                    value={editingConsultant.telephone || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, telephone: e.target.value})}
                    size="small"
                    
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={editingConsultant.email || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, email: e.target.value})}
                    size="small"
                  />
                </Grid>

                {/* Row 4: Contact Person, Region, City */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Contact Person Name"
                    value={editingConsultant.contact_person_name || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, contact_person_name: e.target.value})}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Region"
                    value={editingConsultant.address_region || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, address_region: e.target.value})}
                    size="small"
                  
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={editingConsultant.address_city || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, address_city: e.target.value})}
                    size="small"
                    
                  />
                </Grid>

                {/* Row 5: Sub City, Woreda, House No */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Sub City"
                    value={editingConsultant.address_subcity || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, address_subcity: e.target.value})}
                    size="small"
                    
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Woreda"
                    value={editingConsultant.address_woreda || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, address_woreda: e.target.value})}
                    size="small"
                    
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="House No"
                    value={editingConsultant.address_house_no || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, address_house_no: e.target.value})}
                    size="small"
                    
                  />
                </Grid>

                {/* Row 6: Description - Full Width */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={editingConsultant.description || ''}
                    onChange={(e) => setEditingConsultant({...editingConsultant, description: e.target.value})}
                    multiline
                    rows={3}
                    size="small"
                  />
                </Grid>

                {/* Loading Indicator */}
                {submitting && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2 }}>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      <Typography variant="body2">Updating consultant...</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button 
                  onClick={handleCancelEdit} 
                  variant="outlined"
                  startIcon={<Cancel />}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary"
                  startIcon={<Save />}
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : "Save Changes"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ManageConsultants;